import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { TrendingUp, FolderKanban, Receipt, Clock, ArrowUpRight, CalendarDays, Wallet, Bell, Settings } from "lucide-react";

const WIDGET_LABELS = {
  kpi_overview: "KPI Overview",
  revenue_mtd: "Revenue MTD",
  outstanding_ageing: "Outstanding Ageing",
  missing_status: "Missing Status (Today)",
  pending_approvals: "Pending Approvals",
  monthly_cost: "Monthly Cost Snapshot",
  mis_summary: "MIS Summary (YTD)",
  my_timesheet: "My Timesheet Summary",
  my_leave_balance: "My Leave Balance",
  my_pending_status: "My Pending Status",
  my_team: "My Team",
  my_clients: "My Clients",
  pending_payments: "Pending Payments",
};

function KpiLink({ to, label, value, sub, icon: Icon, testId }) {
  return (
    <Link to={to} className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-md hover:border-slate-300 transition-all block" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="eyebrow">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-[#C2A671]"/>}
      </div>
      <div className="serif-num text-5xl mt-4 text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [missing, setMissing] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [tempPrefs, setTempPrefs] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    if (["partner","admin","manager"].includes(user?.role)) {
      api.get("/project-status-updates?limit=8").then((r) => setStatusUpdates(r.data)).catch(()=>{});
    }
    if (["partner","admin"].includes(user?.role)) {
      api.get("/status-updates/missing-today").then((r) => setMissing(r.data)).catch(()=>{});
    }
    api.get("/dashboard/prefs").then((r) => { setPrefs(r.data); setTempPrefs(r.data.widgets || []); }).catch(()=>{});
  }, [user]);

  const savePrefs = async () => {
    try {
      const r = await api.post("/dashboard/prefs", { widgets: tempPrefs });
      setPrefs(r.data);
      setPrefsOpen(false);
    } catch (e) {}
  };

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  const firm = data.firm;
  const role = user?.role;
  const isPartner = role === "partner";
  const widgetEnabled = (key) => !prefs || (prefs.widgets || []).includes(key);

  return (
    <div className="space-y-8 fade-in" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Dashboard</div>
          <h1 className="font-sans text-4xl mt-2">Hello, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-slate-600 text-sm mt-1">Here's your snapshot.</p>
        </div>
        {prefs && (prefs.available || []).length > 0 && (
          <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-sm" data-testid="customize-dashboard"><Settings className="h-4 w-4 mr-2"/>Customize</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-sans text-xl">Dashboard Widgets</DialogTitle></DialogHeader>
              <div className="space-y-2">
                {(prefs.available || []).map((w) => (
                  <label key={w} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-sm cursor-pointer" data-testid={`widget-toggle-${w}`}>
                    <Checkbox checked={tempPrefs.includes(w)} onCheckedChange={(c)=>setTempPrefs((p)=> c ? [...p, w] : p.filter((x) => x !== w))}/>
                    <span className="text-sm">{WIDGET_LABELS[w] || w}</span>
                  </label>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>{ setTempPrefs(prefs.widgets || []); setPrefsOpen(false); }} className="rounded-sm">Cancel</Button>
                <Button onClick={savePrefs} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="save-widgets">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {missing && missing.count > 0 && widgetEnabled("missing_status") && (
        <Link to="/portal/status" className="block bg-amber-50 border border-amber-200 rounded-sm p-4 hover:bg-amber-100 transition-colors" data-testid="missing-status-banner">
          <div className="flex items-start gap-3">
            <Bell className="h-5 w-5 text-amber-700 shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-900">{missing.count} employee{missing.count !== 1 ? "s" : ""} haven't posted today's status</div>
              <div className="text-xs text-amber-800 mt-1">{missing.missing.slice(0, 5).map((m) => m.name).join(", ")}{missing.missing.length > 5 ? ` and ${missing.missing.length - 5} more` : ""}</div>
            </div>
            <ArrowUpRight className="h-4 w-4 text-amber-700"/>
          </div>
        </Link>
      )}

      {firm && widgetEnabled("kpi_overview") && (
        <section>
          <div className="eyebrow mb-4">Firm overview</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
            <KpiLink to="/portal/projects" label="Active Projects" value={firm.active_projects} sub={`of ${firm.total_projects} total`} icon={FolderKanban} testId="kpi-active-projects"/>
            <KpiLink to="/portal/timesheet" label="Billable Hours · MTD" value={firm.billable_hours_month} sub={`Non-billable: ${firm.non_billable_hours_month}h`} icon={Clock} testId="kpi-billable-hours"/>
            {isPartner && <KpiLink to="/portal/reports" label="Utilization" value={`${firm.utilization_pct}%`} sub="Billable / Total hours" icon={TrendingUp} testId="kpi-utilization"/>}
            <KpiLink to="/portal/approvals" label="Pending Approvals" value={firm.pending_timesheets + firm.pending_reimbursements + firm.pending_leaves} sub={`${firm.pending_timesheets} TS · ${firm.pending_reimbursements} Claims · ${firm.pending_leaves} Leaves`} icon={Receipt} testId="kpi-pending"/>
          </div>
          {firm.project_status_breakdown && Object.keys(firm.project_status_breakdown).length > 0 && (
            <div className="mt-4 bg-white border border-slate-200 rounded-sm p-6" data-testid="project-status-widget">
              <div className="eyebrow mb-4">Projects by status</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                {Object.entries(firm.project_status_breakdown).map(([status, count]) => (
                  <Link to="/portal/projects" key={status} className="bg-white p-4 hover:bg-slate-50 transition-colors">
                    <div className="eyebrow">{status}</div>
                    <div className="serif-num text-3xl mt-2">{count}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <section>
        <div className="eyebrow mb-4">My snapshot</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 stagger">
          <KpiLink to="/portal/timesheet" label="My Hours · MTD" value={data.me.hours_this_month} icon={Clock} testId="kpi-my-hours"/>
          <KpiLink to="/portal/projects" label="My Projects" value={data.me.assigned_projects} icon={FolderKanban} testId="kpi-my-projects"/>
          <KpiLink to="/portal/reimbursements" label="My Pending Claims" value={data.me.pending_claims} icon={Receipt} testId="kpi-my-claims"/>
          <KpiLink to="/portal/leave" label="My Pending Leaves" value={data.me.pending_leaves} icon={CalendarDays} testId="kpi-my-leaves"/>
        </div>
      </section>

      {statusUpdates.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-sm" data-testid="dashboard-status-updates">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div className="eyebrow">Recent Project Status Updates</div>
            <Link to="/portal/projects" className="text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">View projects <ArrowUpRight className="h-3 w-3"/></Link>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {statusUpdates.map((u) => (
              <Link key={u.id} to={`/portal/projects/${u.project_id}`} className="p-4 px-6 flex items-start gap-4 hover:bg-slate-50 transition-colors">
                <div className="w-2 h-2 rounded-full bg-[#C2A671] mt-2 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm"><span className="font-medium">{u.user_name}</span> · {u.project_code} {u.project_name} → <span className="text-xs uppercase tracking-[0.2em] px-2 py-0.5 border rounded-full border-slate-200 bg-slate-50">{u.status}</span></div>
                    <span className="text-xs text-slate-400">{u.created_at?.slice(0,16).replace("T", " ")}</span>
                  </div>
                  {u.notes && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{u.notes}</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-sm border-slate-200">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div className="eyebrow">Recent Timesheets</div>
            <Link to="/portal/timesheet" className="text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">View all <ArrowUpRight className="h-3 w-3"/></Link>
          </div>
          <div className="divide-y divide-slate-100" data-testid="recent-timesheets">
            {data.recent_timesheets.length === 0 && <div className="p-6 text-sm text-slate-500">No entries yet.</div>}
            {data.recent_timesheets.map((t) => (
              <div key={t.id} className="p-4 px-6 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.client_name || "—"} · {t.project_name}</div>
                  <div className="text-xs text-slate-500">{t.task_date} · {t.hours}h · {t.user_name || ""}</div>
                </div>
                <span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full whitespace-nowrap ${t.approval_status === "approved" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : t.approval_status === "rejected" ? "border-red-200 text-red-700 bg-red-50" : t.approval_status === "draft" ? "border-slate-200 text-slate-600 bg-slate-50" : "border-amber-200 text-amber-700 bg-amber-50"}`}>{t.approval_status}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-sm border-slate-200">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div className="eyebrow">Recent Reimbursements</div>
            <Link to="/portal/reimbursements" className="text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">View all <ArrowUpRight className="h-3 w-3"/></Link>
          </div>
          <div className="divide-y divide-slate-100" data-testid="recent-reimbursements">
            {data.recent_reimbursements.length === 0 && <div className="p-6 text-sm text-slate-500">No claims yet.</div>}
            {data.recent_reimbursements.map((r) => (
              <div key={r.id} className="p-4 px-6 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.expense_type} · {r.client_name || "—"}</div>
                  <div className="text-xs text-slate-500">{r.claim_date} · ₹{r.amount} · {r.user_name || ""}</div>
                </div>
                <span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full whitespace-nowrap ${r.status === "approved" || r.status === "paid" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : r.status === "rejected" ? "border-red-200 text-red-700 bg-red-50" : r.status === "draft" ? "border-slate-200 text-slate-600 bg-slate-50" : "border-amber-200 text-amber-700 bg-amber-50"}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
}
